// src/components/Magazine/MagazineShopDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { EditableFormField } from '@/components/admin/EditableFormField';
import { ImageUpload } from '@/components/admin/ImageUpload';
import { AdminLayout } from '@/components/admin/AdminLayout';
import {
  getAdminNeighborhoodDetail,
  patchAdminNeighborhood,
} from '@/API/apiService';
import { DatePickerField } from '@/components/admin/DatePickerField';
import ChevronLeftIcon from '@/image/common/ChevronLeftIcon';
import {
  useNeighborhoodList,
  usePropertiesList,
  Property,
  Area,
  Tag,
} from '@/components/admin/DropDowns';
import { MultiDropdownField } from '@/components/admin/Common/MultiDropdownField';

interface FormState {
  id: number;
  title: string; // 施設名 (日本語)
  title_en: string; // 施設名 (英語)
  date_time: string;
  information: string; // 店舗情報 (日本語)
  information_en: string; // 店舗情報 (英語)
  body: string; // 本文 (日本語)
  body_en: string; // 本文 (英語)
  // 物件／エリア／カテゴリタグの値（数値のIDを文字列で保持）
  property1: string;
  property2: string;
  property3: string;
  areaTag1: string;
  areaTag2: string;
  areaTag3: string;
  categoryTag1: string;
  categoryTag2: string;
  categoryTag3: string;
  categoryTag4: string;
  categoryTag5: string;
  categoryTag6: string;
  title_text: string; // 概要 (日本語)
  title_text_en: string; // 概要 (英語)
}

export const MagazineShopDetail: React.FC = () => {
  const { shop_id } = useParams<{ shop_id?: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormState>({
    id: 0,
    title: '',
    title_en: '',
    date_time: '',
    information: '',
    information_en: '',
    body: '',
    body_en: '',
    property1: '',
    property2: '',
    property3: '',
    areaTag1: '',
    areaTag2: '',
    areaTag3: '',
    categoryTag1: '',
    categoryTag2: '',
    categoryTag3: '',
    categoryTag4: '',
    categoryTag5: '',
    categoryTag6: '',
    title_text: '',
    title_text_en: '',
  });

  // 物件（紐付け物件）の元データを保持する
  const [originalProperties, setOriginalProperties] = useState<number[]>([]);
  // エリアの元データ（初期選択済みエリア）のID 配列を保持
  const [originalAreas, setOriginalAreas] = useState<number[]>([]);
  // カテゴリタグの元データ（初期選択済みカテゴリ）のID 配列を保持
  const [originalCategories, setOriginalCategories] = useState<number[]>([]);

  // トップ画像
  const [headerImageFile, setHeaderImageFile] = useState<File | null>(null);
  const [headerImagePreview, setHeaderImagePreview] = useState<string>('');

  // その他画像
  const [originalOtherImages, setOriginalOtherImages] = useState<string[]>([]);
  const [newOtherImageFiles, setNewOtherImageFiles] = useState<File[]>([]);
  const [newOtherImagePreviews, setNewOtherImagePreviews] = useState<string[]>(
    []
  );

  const [loading, setLoading] = useState<boolean>(false);

  // ドロップダウン用のオプション取得
  const {
    areas,
    tags,
    loading: dropdownLoading,
    error: dropdownError,
  } = useNeighborhoodList();
  const {
    properties,
    loading: propertiesLoading,
    error: propertiesError,
  } = usePropertiesList();

  useEffect(() => {
    const fetchDetail = async () => {
      if (shop_id) {
        setLoading(true);
        try {
          const response = await getAdminNeighborhoodDetail(shop_id);
          const data = response.data.data || response.data;

          // 物件情報の初期値：API の properties は { id, name, name_en } の形式の場合
          if (Array.isArray(data.properties)) {
            const propIds: number[] = data.properties
              .filter((prop: any) => prop.id)
              .map((prop: any) => prop.id);
            setOriginalProperties(propIds);
            setFormData((prev) => ({
              ...prev,
              property1: propIds[0] ? String(propIds[0]) : '',
              property2: propIds[1] ? String(propIds[1]) : '',
              property3: propIds[2] ? String(propIds[2]) : '',
            }));
          }

          // エリア情報の初期値
          if (Array.isArray(data.areas)) {
            const areaIds: number[] = data.areas
              .filter((area: any) => area.id)
              .map((area: any) => area.id);
            setOriginalAreas(areaIds);
            setFormData((prev) => ({
              ...prev,
              areaTag1: areaIds[0] ? String(areaIds[0]) : '',
              areaTag2: areaIds[1] ? String(areaIds[1]) : '',
              areaTag3: areaIds[2] ? String(areaIds[2]) : '',
            }));
          }

          // カテゴリタグの初期値
          if (Array.isArray(data.tags)) {
            const tagIds: number[] = data.tags
              .filter((tag: any) => tag.id)
              .map((tag: any) => tag.id);
            setOriginalCategories(tagIds);
            setFormData((prev) => ({
              ...prev,
              categoryTag1: tagIds[0] ? String(tagIds[0]) : '',
              categoryTag2: tagIds[1] ? String(tagIds[1]) : '',
              categoryTag3: tagIds[2] ? String(tagIds[2]) : '',
              categoryTag4: tagIds[3] ? String(tagIds[3]) : '',
              categoryTag5: tagIds[4] ? String(tagIds[4]) : '',
              categoryTag6: tagIds[5] ? String(tagIds[5]) : '',
            }));
          }

          // その他の項目（タイトル、説明、など）
          setFormData((prev) => ({
            ...prev,
            id: data.id || 0,
            title: data.title || '',
            title_en: data.title_en || '',
            date_time: data.date_time || '',
            information: data.information || '',
            information_en: data.information_en || '',
            body: data.body || '',
            body_en: data.body_en || '',
            title_text: data.title_text || '',
            title_text_en: data.title_text_en || '',
          }));

          // ヘッダー画像／その他画像の更新
          setHeaderImagePreview(data.header_image_url || '');
          if (Array.isArray(data.images)) {
            const urls = data.images.map((img: any) => img.url || '');
            setOriginalOtherImages(urls);
          }
        } catch (err) {
          console.error('Error fetching details:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchDetail();
  }, [shop_id]);

  /**
   * フォーム更新
   */
  const handleInputChange = (field: keyof FormState, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * トップ画像(1枚)の変更
   */
  const handleHeaderImageChange = (files: File[]) => {
    if (files.length > 0) {
      const file = files[0];
      setHeaderImageFile(file);
      setHeaderImagePreview(URL.createObjectURL(file));
    }
  };

  /**
   * トップ画像の削除
   */
  const handleRemoveHeaderImage = () => {
    setHeaderImageFile(null);
    setHeaderImagePreview('');
  };

  /**
   * その他画像(複数)の追加
   */
  const handleOtherImagesChange = (files: File[]) => {
    const newFiles = Array.from(files);
    setNewOtherImageFiles((prev) => [...prev, ...newFiles]);
    const previews = newFiles.map((file) => URL.createObjectURL(file));
    setNewOtherImagePreviews((prev) => [...prev, ...previews]);
  };

  /**
   * その他画像の削除
   */
  const handleRemoveOtherImage = (index: number) => {
    const originalCount = originalOtherImages.length;
    if (index < originalCount) {
      setOriginalOtherImages((prev) => {
        const newArr = [...prev];
        newArr.splice(index, 1);
        return newArr;
      });
      // 既存画像削除のロジックがあれば、ここで差分管理も実施
    } else {
      const newIndex = index - originalCount;
      setNewOtherImagePreviews((prev) => {
        const newArr = [...prev];
        newArr.splice(newIndex, 1);
        return newArr;
      });
      setNewOtherImageFiles((prev) => {
        const newArr = [...prev];
        newArr.splice(newIndex, 1);
        return newArr;
      });
    }
  };

  /**
   * 保存処理
   */
  const handleSaveAll = async () => {
    if (!shop_id) {
      console.error('shop_id is not specified.');
      return;
    }
    setLoading(true);

    try {
      const fd = new FormData();

      // テキスト系の項目を FormData に追加
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'date_time' && value) {
            const parts = value.toString().split('.');
            if (parts.length === 3) {
              const [year, month, day] = parts;
              fd.append(
                key,
                `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
              );
            } else {
              fd.append(key, value.toString());
            }
          } else {
            fd.append(key, value.toString());
          }
        }
      });

      // 画像関連
      if (headerImageFile) {
        fd.append('header_image', headerImageFile);
      }
      newOtherImageFiles.forEach((file) => {
        fd.append('add_images[]', file);
      });
      originalOtherImages.forEach((url, i) => {
        fd.append(`remain_images[${i}]`, url);
      });

      // 差分計算：紐付け物件の更新
      const currentProperties = [
        formData.property1,
        formData.property2,
        formData.property3,
      ]
        .filter((val) => val && val.trim() !== '')
        .map((val) => Number(val));
      const addProperties = currentProperties.filter(
        (id) => !originalProperties.includes(id)
      );
      const deleteProperties = originalProperties.filter(
        (id) => !currentProperties.includes(id)
      );
      addProperties.forEach((id) =>
        fd.append('add_properties[]', id.toString())
      );
      deleteProperties.forEach((id) =>
        fd.append('delete_properties[]', id.toString())
      );

      // 差分計算：エリアタグの更新
      const currentAreas = [
        formData.areaTag1,
        formData.areaTag2,
        formData.areaTag3,
      ]
        .filter((val) => val && val.trim() !== '')
        .map((val) => Number(val));
      const addAreas = currentAreas.filter((id) => !originalAreas.includes(id));
      const deleteAreas = originalAreas.filter(
        (id) => !currentAreas.includes(id)
      );
      addAreas.forEach((id) => fd.append('add_areas[]', id.toString()));
      deleteAreas.forEach((id) => fd.append('delete_areas[]', id.toString()));

      // 差分計算：カテゴリタグの更新
      const currentCategories = [
        formData.categoryTag1,
        formData.categoryTag2,
        formData.categoryTag3,
        formData.categoryTag4,
        formData.categoryTag5,
        formData.categoryTag6,
      ]
        .filter((val) => val && val.trim() !== '')
        .map((val) => Number(val));
      const addCategories = currentCategories.filter(
        (id) => !originalCategories.includes(id)
      );
      const deleteCategories = originalCategories.filter(
        (id) => !currentCategories.includes(id)
      );
      addCategories.forEach((id) => fd.append('add_tags[]', id.toString()));
      deleteCategories.forEach((id) =>
        fd.append('delete_tags[]', id.toString())
      );

      // ※ 他（必要な項目）があればここに差分計算を追加

      const response = await patchAdminNeighborhood(shop_id, fd);
      alert('編集が成功しました。');
      // 必要に応じて一覧画面へ戻す等の処理を追加
      // navigate('/admin/magazine');
    } catch (err) {
      console.error('Update failed:', err);
      alert('更新に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const requiredLabel = (label: string) => (
    <span>
      {label}
      <span style={{ color: 'red' }}>*</span>
    </span>
  );

  if (loading || dropdownLoading || propertiesLoading)
    return <div>Loading...</div>;
  if (dropdownError || propertiesError)
    return <div>オプションの読み込みエラー</div>;

  // その他画像をまとめる
  const allOtherImages = [...originalOtherImages, ...newOtherImagePreviews];

  return (
    <AdminLayout>
      <div className="p-6 bg-yellow">
        {/* アイコン＋タイトル */}
        <div className="flex items-center pb-6">
          <span className="bg-gray-50 border border-black p-2 rounded">
            <Link to="/admin/magazine">
              <ChevronLeftIcon />
            </Link>
          </span>
          <span className="text-2xl ml-3">
            {formData.title}/{formData.title_en}
          </span>
        </div>

        {/* 施設名 */}
        <EditableFormField
          label="施設名"
          field="title"
          subLabel1="(日本語表記)"
          defaultValue1={formData.title}
          subLabel2="(英語表記)"
          defaultValue2={formData.title_en}
          height={33}
          onValueChange={(values) => {
            handleInputChange('title', values.value1 || '');
            handleInputChange('title_en', values.value2 || '');
          }}
        />

        {/* 更新日 */}
        <DatePickerField
          label={requiredLabel('更新日')}
          value={formData.date_time}
          onChange={(newValue) => handleInputChange('date_time', newValue)}
        />

        {/* 店舗情報 */}
        <EditableFormField
          label="店舗情報"
          field="information"
          subLabel1="(日本語表記)"
          defaultValue1={formData.information}
          subLabel2="(英語表記)"
          defaultValue2={formData.information_en}
          height={100}
          onValueChange={(values) => {
            handleInputChange('information', values.value1 || '');
            handleInputChange('information_en', values.value2 || '');
          }}
        />

        {/* 本文 */}
        <EditableFormField
          label="本文"
          field="body"
          subLabel1="(日本語表記)"
          defaultValue1={formData.body}
          subLabel2="(英語表記)"
          defaultValue2={formData.body_en}
          height={150}
          onValueChange={(values) => {
            handleInputChange('body', values.value1 || '');
            handleInputChange('body_en', values.value2 || '');
          }}
        />

        {/* 紐付け物件 → MultiDropdownField を利用 */}
        <MultiDropdownField
          label="紐付け物件"
          count={3}
          values={[formData.property1, formData.property2, formData.property3]}
          options={properties.map((prop: Property) => ({
            id: prop.property_id,
            name: prop.property_name,
          }))}
          height={33}
          onChange={(newValues) => {
            handleInputChange('property1', newValues[0] || '');
            handleInputChange('property2', newValues[1] || '');
            handleInputChange('property3', newValues[2] || '');
          }}
        />

        {/* エリアタグ → MultiDropdownField を利用 */}
        <MultiDropdownField
          label="エリアタグ"
          count={3}
          values={[formData.areaTag1, formData.areaTag2, formData.areaTag3]}
          options={areas.map((area: Area) => ({
            id: area.id,
            name: area.name,
          }))}
          height={33}
          onChange={(newValues) => {
            handleInputChange('areaTag1', newValues[0] || '');
            handleInputChange('areaTag2', newValues[1] || '');
            handleInputChange('areaTag3', newValues[2] || '');
          }}
        />

        {/* カテゴリタグ → MultiDropdownField を利用 */}
        <MultiDropdownField
          label="カテゴリタグ"
          count={6}
          values={[
            formData.categoryTag1,
            formData.categoryTag2,
            formData.categoryTag3,
            formData.categoryTag4,
            formData.categoryTag5,
            formData.categoryTag6,
          ]}
          options={tags.map((tag: Tag) => ({
            id: tag.id,
            name: tag.name,
          }))}
          height={33}
          onChange={(newValues) => {
            handleInputChange('categoryTag1', newValues[0] || '');
            handleInputChange('categoryTag2', newValues[1] || '');
            handleInputChange('categoryTag3', newValues[2] || '');
            handleInputChange('categoryTag4', newValues[3] || '');
            handleInputChange('categoryTag5', newValues[4] || '');
            handleInputChange('categoryTag6', newValues[5] || '');
          }}
        />

        {/* 概要 */}
        <EditableFormField
          label="概要"
          field="title_text"
          subLabel1="(日本語表記)"
          defaultValue1={formData.title_text}
          subLabel2="(英語表記)"
          defaultValue2={formData.title_text_en}
          height={150}
          onValueChange={(values) => {
            handleInputChange('title_text', values.value1 || '');
            handleInputChange('title_text_en', values.value2 || '');
          }}
        />

        {/* トップ画像 */}
        <ImageUpload
          label="トップ画像"
          images={
            headerImagePreview ? [{ id: 0, url: headerImagePreview }] : []
          }
          onAddImages={handleHeaderImageChange}
          onRemoveImage={handleRemoveHeaderImage}
        />

        {/* 写真登録 */}
        <ImageUpload
          label="写真登録"
          images={allOtherImages.map((url, idx) => ({ id: idx, url }))}
          onAddImages={handleOtherImagesChange}
          onRemoveImage={handleRemoveOtherImage}
        />

        {/* 保存ボタン */}
        <div className="flex justify-center mt-6">
          <button
            type="button"
            onClick={handleSaveAll}
            className="px-4 py-2 bg-blue-500 text-white rounded"
          >
            編集した項目を更新
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default MagazineShopDetail;
